import { api, API_ENDPOINTS } from '../lib/api';
import {
  Transaction,
  StartChargingRequest,
  StartChargingResponse,
  StopChargingRequest,
  StopChargingResponse,
  PaginatedResponse,
} from '../types';

/**
 * Query parameters for fetching transactions
 */
export interface GetTransactionsParams {
  page?: number;
  limit?: number;
  status?: 'Active' | 'Completed' | 'Failed' | 'Stopped';
}

/**
 * Transaction Service
 * Handles charging transaction operations: start, stop, and fetch transactions
 */
export const transactionService = {
  /**
   * Start a charging session
   * @param data - Location, charge point, connector, and vehicle information
   * @returns Transaction ID and status
   */
  startCharging: async (data: StartChargingRequest): Promise<StartChargingResponse> => {
    const response = await api.post<StartChargingResponse>(API_ENDPOINTS.TRANSACTIONS.START, data);
    return response.data;
  },

  /**
   * Stop a charging session
   * @param data - Transaction ID to stop
   * @returns Transaction summary with energy consumed and total cost
   */
  stopCharging: async (data: StopChargingRequest): Promise<StopChargingResponse> => {
    const response = await api.post<StopChargingResponse>(API_ENDPOINTS.TRANSACTIONS.STOP, data);
    return response.data;
  },

  /**
   * Get all transactions for the current user (with pagination)
   * @param params - Pagination and filtering parameters
   * @returns Paginated list of transactions
   */
  getMyTransactions: async (params?: GetTransactionsParams): Promise<PaginatedResponse<Transaction>> => {
    const response = await api.get<PaginatedResponse<Transaction>>(
      API_ENDPOINTS.TRANSACTIONS.MY_TRANSACTIONS,
      {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          status: params?.status,
        },
      }
    );
    return response.data;
  },

  /**
   * Get active (ongoing) transactions for the current user
   * @returns Array of active transactions
   */
  getActiveTransactions: async (): Promise<Transaction[]> => {
    // Use my-transactions endpoint with isActive filter instead of non-existent /active endpoint
    const response = await api.get<PaginatedResponse<Transaction>>(
      API_ENDPOINTS.TRANSACTIONS.MY_TRANSACTIONS,
      {
        params: {
          isActive: true,
          page: 1,
          limit: 10, // Active transactions should be limited (typically only 1 active at a time)
        },
      }
    );
    return response.data.data; // Extract data array from paginated response
  },

  /**
   * Get transaction by ID with real-time metrics
   * Used for polling during charging to get current power, SOC, energy consumed
   * @param transactionId - Transaction UUID
   * @returns Transaction with real-time metrics
   */
  getTransactionById: async (transactionId: string): Promise<Transaction> => {
    const response = await api.get<Transaction>(API_ENDPOINTS.TRANSACTIONS.BY_ID(transactionId));
    return response.data;
  },
};
